/**
 * The remark/rehype plugin set every chat-shaped markdown surface renders with.
 *
 * Exists so the surfaces cannot drift apart. Before this, chat ran
 * `remarkPlugins={[remarkGfm]}` and nothing else, while the TipTap note editor
 * loaded `@tiptap/extension-mathematics` — so the same equation rendered in a
 * note and came out as raw `$SiO_2$` in Bird's reply. Bird writes inline LaTeX
 * heavily and unprompted (a geochemistry pilot's answers are full of
 * `$\mu^{66}Zn$`, `$10.6\text{ wt}\%$`, `$^{84}$Sr`), so every one of those was
 * shown to the user as literal markup.
 *
 * Import these arrays rather than re-listing plugins at a call site: a surface
 * that lists its own is a surface that will be forgotten the next time this set
 * changes.
 *
 * Ordering is load-bearing:
 *  - `remarkMath` must precede `remarkDemoteCurrencyMath`, which inspects the
 *    `inlineMath` nodes the former produces.
 *  - `rehypeKatex` runs on the rehype side, after the whole remark chain.
 */

import type { ComponentProps } from 'react';
import type ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';

import { remarkDemoteCurrencyMath } from './mathGuard';

// Taken off the component's own props rather than imported from `unified` or
// `react-markdown/lib`: neither is a direct dependency, and react-markdown's
// package exports expose only its root entry point.
type RemarkPlugins = NonNullable<ComponentProps<typeof ReactMarkdown>['remarkPlugins']>;
type RehypePlugins = NonNullable<ComponentProps<typeof ReactMarkdown>['rehypePlugins']>;

// KaTeX ships its own stylesheet; without it every rendered formula falls back
// to unstyled spans that read worse than the raw markup did. It is NOT imported
// here: this module is on the eager path, and a static CSS import would make it
// render-blocking on every page. `BirdMessage` requests it on demand through
// `utils/katexStyles.ts`.

export const CHAT_REMARK_PLUGINS: RemarkPlugins = [remarkGfm, remarkMath, remarkDemoteCurrencyMath];

export const CHAT_REHYPE_PLUGINS: RehypePlugins = [
  [
    rehypeKatex,
    {
      // Never let a malformed formula take the message down with it. Bird
      // emits LaTeX it has not validated, and a throw here would blank the
      // whole reply; `throwOnError: false` renders the offending source in
      // `errorColor` instead, which is legible and self-evidently wrong.
      throwOnError: false,
      // Bird's chemistry notation leans on \ce-adjacent macros and text mode;
      // trusting nothing keeps \href and \includegraphics inert.
      trust: false,
      strict: false,
    },
  ],
];
